import React, { useMemo } from "react";
import { Table } from "@prestosports/commons-ui";
import Checkbox from "@material-ui/core/Checkbox";
import { Box } from "@material-ui/core";
import { PlayerStatus } from "@prestosports/prestostats-core/Common";
import Warning24 from "@carbon/icons-react/lib/warning/24";
import { usePlayersContext } from "../../../state/players/context";
import PlayerFormat from "../../../util/player-format";
import playerListTableStyles from "../../team/styles/PlayerListTable.styles";
import { useFlagFootballTeamContext } from "../../../state/team-context/flag-football-context";
import { useAppContext } from "../../../state/app/context";

const playerListColumns = ({
	toggleStarter,
	toggleParticipant,
	toggleActive,
	setCaptain
}) => {
	return [
		{
			label: "NUMBER",
			sortId: "uniform",
			component: ({ data: player }) =>
				`${player.uniform || ""}${
					PlayerStatus.getStatusInactive(player.playerStatus) ? " (Inactive)" : ""
				}`
		},
		{
			label: "FIRST NAME",
			id: "firstName",
			sortId: "firstName",
			component: ({ data: player }) => player.firstName || ""
		},
		{
			label: "LAST NAME",
			id: "lastName",
			sortId: "lastName",
			component: ({ data: player }) => player.lastName || ""
		},
		{
			label: "POSITION",
			id: "position",
			sortId: "position",
			component: ({ data: player }) =>
				player.positions
					? player.positions
							.filter((p) => p)
							.map((p) => p && p.name)
							.join(",")
					: ""
		},
		{
			label: "ACTIVE",
			id: "active",
			align: "center",
			sortId: "active",
			component: ({ data: player }) => (
				<Checkbox checked={player.active} onChange={() => toggleActive(player)} />
			),
			hidden: true
		},
		{
			label: "STARTER",
			id: "starter",
			align: "center",
			sortId: "starter",
			component: ({ data: player }) => (
				<Checkbox checked={player.starter} onChange={() => toggleStarter(player)} />
			),
			hidden: true
		},
		{
			label: "PARTICIPANT",
			id: "participant",
			align: "center",
			alignHeader: "center",
			sortId: "participant",
			component: ({ data: player }) => (
				<Checkbox
					disabled={PlayerStatus.getStatusInactive(player.playerStatus)}
					checked={player.participant}
					onChange={() => toggleParticipant(player)}
				/>
			)
		},
		{
			label: "CAPTAIN",
			id: "captain",
			align: "center",
			sortId: "captain",
			component: ({ data: player }) => (
				<Checkbox checked={player.captain} onChange={() => setCaptain(player)} />
			),
			hidden: true
		}
	].filter((c) => !c.hidden);
};

const PlayerList = ({
	team,
	onRowAction,
	onSelectionChanged,
	selectedPlayerIds,
	onPageChanged,
	currentPage,
	onSortChanged,
	sortField = "uniform",
	sortDirection = "desc",
	playerType = PlayerStatus.ACTIVE
}) => {
	const classes = playerListTableStyles();

	const {
		state: teamStatsState,
		toggleStarter,
		toggleParticipant,
		toggleActive,
		setCaptain
	} = useFlagFootballTeamContext({
		team
	});

	const { state: appContextState } = useAppContext();
	const playerManagement = appContextState.playerManagement();

	const { state: playersState } = usePlayersContext();

	const starters = teamStatsState.starters() || [];
	const participants = teamStatsState.participants() || [];
	const activePlayers = teamStatsState.activePlayers() || [];
	const captain = teamStatsState.captain();

	const players = playersState.players(team.id);
	const hiddenUniforms = [
		PlayerFormat.TeamUniform,
		PlayerFormat.NotSpecifiedUniform
	];

	const getPlayerStatus = ({ playerStatus }) => {
		return playerStatus || PlayerStatus.ACTIVE;
	};

	const extendedPlayers = players
		.filter(
			(p) =>
				!hiddenUniforms.includes(p.uniform) && playerType === getPlayerStatus(p)
		)
		.map((player) => {
			return {
				...player,
				active: !!activePlayers.find((p) => p === player.id),
				starter: !!starters.find((p) => p === player.id),
				participant: !!participants.find((p) => p === player.id),
				captain: player.id === captain
			};
		});

	const tableColumns = useMemo(
		() =>
			playerListColumns({
				toggleStarter,
				toggleParticipant,
				toggleActive,
				setCaptain
			}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	const handleOnRowAction = (row) => {
		if (!PlayerStatus.getStatusInactive(row.playerStatus)) {
			onRowAction(row);
		}
	};

	return (
		<Box className={classes.root}>
			<Table
				columns={tableColumns}
				rows={extendedPlayers}
				initialRowsPerPage={25}
				rowsPerPageOptionsEnabled
				sortField={sortField}
				sortDirection={sortDirection}
				sortComparators={{
					uniform: PlayerFormat.compareUniforms
				}}
				handleRequestSortCallback={onSortChanged}
				multiSelect={
					playerManagement ? { onSelect: onSelectionChanged, rowId: "id" } : {}
				}
				rowAction={playerManagement && handleOnRowAction}
				rowActionDisabled={(row) =>
					PlayerStatus.getStatusInactive(row.playerStatus)
				}
				handleChangePageCallback={onPageChanged}
				selectedRows={selectedPlayerIds}
				selectionDisabled={(row) => {
					return PlayerStatus.getStatusInactive(row.playerStatus) || undefined;
				}}
				DisabledSelectionIcon={<Warning24 />}
				currentPage={currentPage}
			/>
		</Box>
	);
};

export default PlayerList;
